import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  getFormTemplates,
  createFormTemplate,
  updateFormTemplate,
  deleteFormTemplate,
  getFournisseurs,
} from "../api/api";

import { useNotifications } from "../components/NotificationsContext";

// ---------------- Helpers ----------------

const slugKey = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 50);

const safeArray = (v) => (Array.isArray(v) ? v : []);

const SIMPLE_FIELD_TYPES = [
  { value: "text", label: "Texte" },
  { value: "number", label: "Nombre" },
  { value: "date", label: "Date" },
  { value: "textarea", label: "Texte long" },
  { value: "select", label: "Liste déroulante" },
  { value: "radio", label: "Choix unique" },
  { value: "checkbox", label: "Case à cocher" },
  { value: "file", label: "Fichier" },
  { value: "adherent_child_select", label: "Enfant de l’adhérent" },
  { value: "adherent_parent_select", label: "Parent de l’adhérent" },
];

const FIELD_TYPES = [
  ...SIMPLE_FIELD_TYPES,
  { value: "repeatable_group", label: "Liste repetitive" },
];

function fieldTypeHelp(type) {
  switch (type) {
    case "text":
      return "Pour une reponse courte.";
    case "number":
      return "Pour une quantite, un prix ou un nombre.";
    case "date":
      return "Pour demander une date.";
    case "textarea":
      return "Pour une reponse longue.";
    case "select":
      return "L'adherent choisit une seule valeur dans une liste.";
    case "radio":
      return "L'adherent choisit une seule valeur parmi plusieurs choix visibles.";
    case "checkbox":
      return "Pour une confirmation oui/non.";
    case "file":
      return "Pour demander une piece jointe.";
    case "adherent_child_select":
      return "L'adherent choisit un enfant depuis son profil.";
    case "adherent_parent_select":
      return "L'adherent choisit un parent depuis son profil.";
    case "repeatable_group":
      return "Pour repeter les memes questions plusieurs fois, par exemple une ligne par article.";
    default:
      return "";
  }
}

function emptyField() {
  return {
    key: "",
    type: "text",
    label: "",
    required: false,
    optionsText: "",
    count_from: "",
    fields: [],
  };
}

function emptyChildField() {
  return {
    key: "",
    type: "text",
    label: "",
    required: false,
    optionsText: "",
  };
}

function mapOptionsTextToOptions(optionsText = "") {
  return (optionsText || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, value] = line.split(":").map((x) => (x || "").trim());
      return { label: label || value, value: value || label };
    })
    .filter((o) => o.label && o.value);
}

function childFieldsToSchema(fields = []) {
  return fields.map((f) => {
    const field = {
      key: f.key.trim(),
      type: f.type,
      label: f.label.trim(),
      required: Boolean(f.required),
    };

    if (f.type === "select" || f.type === "radio") {
      field.options = mapOptionsTextToOptions(f.optionsText);
    }

    return field;
  });
}

function fieldsToSchema(fields, version = 1) {
  const mapped = fields.map((f) => {
    if (f.type === "repeatable_group") {
      return {
        key: f.key.trim(),
        type: "repeatable_group",
        label: f.label.trim(),
        required: Boolean(f.required),
        count_from: (f.count_from || "").trim(),
        fields: childFieldsToSchema(f.fields || []),
      };
    }

    const field = {
      key: f.key.trim(),
      type: f.type,
      label: f.label.trim(),
      required: Boolean(f.required),
    };

    if (f.type === "select" || f.type === "radio") {
      field.options = mapOptionsTextToOptions(f.optionsText);
    }

    return field;
  });

  return { fields: mapped, version };
}

function schemaToFields(schema) {
  const fields = safeArray(schema?.fields).map((f) => {
    if (f.type === "repeatable_group") {
      return {
        key: f.key || "",
        type: "repeatable_group",
        label: f.label || "",
        required: Boolean(f.required),
        optionsText: "",
        count_from: f.count_from || "",
        fields: safeArray(f.fields).map((child) => ({
          key: child.key || "",
          type: child.type || "text",
          label: child.label || "",
          required: Boolean(child.required),
          optionsText:
            child?.options?.map((o) => `${o.label}:${o.value}`).join("\n") || "",
        })),
      };
    }

    return {
      key: f.key || "",
      type: f.type || "text",
      label: f.label || "",
      required: Boolean(f.required),
      optionsText: f?.options?.map((o) => `${o.label}:${o.value}`).join("\n") || "",
      count_from: "",
      fields: [],
    };
  });

  return fields.length ? fields : [emptyField()];
}

// ---------------- Page ----------------
export default function FormTemplatesList() {
  const { addNotification } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: "",
    fournisseur: "",
    is_active: true,
    version: 1,
    fields: [emptyField()],
  });

  const [errors, setErrors] = useState({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tplRes, frRes] = await Promise.all([
        getFormTemplates(),
        getFournisseurs(),
      ]);

      setTemplates(safeArray(tplRes.data));
      setFournisseurs(safeArray(frRes.data));
    } catch (err) {
      console.error("❌ fetchAll error:", err?.response?.data || err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Impossible de charger les formulaires ou fournisseurs.",
        delay: 7000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fournisseurLabel = useMemo(() => {
    const map = new Map();
    fournisseurs.forEach((f) => {
      const label = f.name ? `${f.name} — ${f.login}` : (f.login || `#${f.id}`);
      map.set(String(f.id), label);
    });
    return map;
  }, [fournisseurs]);

  const openCreate = () => {
    setEditing(null);
    setErrors({});
    setForm({
      name: "",
      fournisseur: "",
      is_active: true,
      version: 1,
      fields: [emptyField()],
    });
    setShowModal(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setErrors({});
    setForm({
      name: tpl.name || "",
      fournisseur: String(tpl.fournisseur || ""),
      is_active: Boolean(tpl.is_active),
      version: Number(tpl?.schema?.version || 1),
      fields: schemaToFields(tpl?.schema),
    });
    setShowModal(true);
  };

  const validate = () => {
    const e = {};

    if (!form.name.trim()) e.name = "Nom obligatoire";
    if (!form.fournisseur) e.fournisseur = "Fournisseur obligatoire";

    if (!form.fields.length) {
      e.fields = "Ajoute au moins un champ";
    }

    const keys = new Set();

    for (const f of form.fields) {
      if (!f.key.trim() || !f.label.trim()) {
        e.fields = "Chaque champ doit avoir un label";
        break;
      }

      if (keys.has(f.key.trim())) {
        e.fields = `La question "${f.label || f.key}" existe deja. Change son nom.`;
        break;
      }
      keys.add(f.key.trim());

      if (f.type === "repeatable_group") {
        if (!f.count_from?.trim()) {
          e.fields = `La liste repetitive "${f.label || f.key}" doit indiquer combien de lignes il faut remplir.`;
          break;
        }

        if (!Array.isArray(f.fields) || !f.fields.length) {
          e.fields = `La liste repetitive "${f.label || f.key}" doit contenir au moins une question.`;
          break;
        }

        const childKeys = new Set();

        for (const child of f.fields) {
          if (!child.key.trim() || !child.label.trim()) {
            e.fields = `Chaque question dans la liste "${f.label || f.key}" doit avoir un nom.`;
            break;
          }

          if (childKeys.has(child.key.trim())) {
            e.fields = `La question "${child.label || child.key}" est utilisee plusieurs fois dans la liste "${f.label || f.key}".`;
            break;
          }
          childKeys.add(child.key.trim());
        }

        if (e.fields) break;
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const schema = fieldsToSchema(form.fields, form.version);

      const payload = {
        name: form.name.trim(),
        fournisseur: parseInt(form.fournisseur, 10),
        is_active: Boolean(form.is_active),
        schema,
      };

      if (!editing) {
        await createFormTemplate(payload);
        addNotification({
          type: "success",
          title: "Créé",
          message: "Formulaire enregistre avec succes.",
          delay: 4000,
        });
      } else {
        await updateFormTemplate(editing.id, payload);
        addNotification({
          type: "success",
          title: "Modifié",
          message: "Formulaire modifie avec succes.",
          delay: 4000,
        });
      }

      setShowModal(false);
      await fetchAll();
    } catch (err) {
      console.error("❌ save template error:", err?.response?.data || err);
      const data = err?.response?.data || {};
      setErrors(data);

      addNotification({
        type: "error",
        title: "Erreur",
        message:
          data?.detail ||
          data?.name?.[0] ||
          data?.fournisseur?.[0] ||
          "Sauvegarde échouée.",
        delay: 7000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!tpl?.id) return;
    if (!window.confirm(`Supprimer le formulaire "${tpl.name}" ?`)) return;

    try {
      await deleteFormTemplate(tpl.id);
      setTemplates((prev) => prev.filter((x) => x.id !== tpl.id));

      addNotification({
        type: "success",
        title: "Suppression",
        message: "Formulaire supprime.",
        delay: 3500,
      });
    } catch (err) {
      console.error("❌ delete template error:", err?.response?.data || err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Suppression échouée.",
        delay: 7000,
      });
    }
  };

  const updateField = (index, patch) => {
    setForm((p) => {
      const next = [...p.fields];
      next[index] = { ...next[index], ...patch };
      return { ...p, fields: next };
    });
  };

  const addField = () =>
    setForm((p) => ({ ...p, fields: [...p.fields, emptyField()] }));

  const removeField = (index) =>
    setForm((p) => ({
      ...p,
      fields: p.fields.length <= 1 ? p.fields : p.fields.filter((_, i) => i !== index),
    }));

  const addChildField = (fieldIndex) => {
    setForm((p) => {
      const next = [...p.fields];
      next[fieldIndex] = {
        ...next[fieldIndex],
        fields: [...(next[fieldIndex].fields || []), emptyChildField()],
      };
      return { ...p, fields: next };
    });
  };

  const updateChildField = (fieldIndex, childIndex, patch) => {
    setForm((p) => {
      const next = [...p.fields];
      const children = [...(next[fieldIndex].fields || [])];
      children[childIndex] = { ...children[childIndex], ...patch };
      next[fieldIndex] = { ...next[fieldIndex], fields: children };
      return { ...p, fields: next };
    });
  };

  const removeChildField = (fieldIndex, childIndex) => {
    setForm((p) => {
      const next = [...p.fields];
      const children = (next[fieldIndex].fields || []).filter((_, i) => i !== childIndex);
      next[fieldIndex] = { ...next[fieldIndex], fields: children };
      return { ...p, fields: next };
    });
  };

  return (
    <Container className="py-4" style={{ maxWidth: 1180 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Formulaires</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Création/édition des formulaires par fournisseur.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={fetchAll}>
            🔄 Rafraîchir
          </Button>
          <Button variant="success" onClick={openCreate}>
            Nouveau formulaire
          </Button>
        </div>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <div style={{ fontSize: 14 }}>
            <b>Astuce :</b> ecris simplement les questions que l'adherent doit remplir.
            Pour une liste repetitive, ajoute d'abord une question de type <b>Nombre</b>,
            puis ajoute une <b>Liste repetitive</b> et choisis cette question comme compteur.
          </div>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : templates.length === 0 ? (
        <Alert variant="warning">
          Aucun formulaire. Clique <b>Nouveau formulaire</b>.
        </Alert>
      ) : (
        <Card>
          <Card.Body>
            <Table bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Fournisseur</th>
                  <th>Actif</th>
                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td style={{ fontWeight: 700 }}>{t.name}</td>
                    <td>{fournisseurLabel.get(String(t.fournisseur)) || t.fournisseur}</td>
                    <td>
                      {t.is_active ? (
                        <Badge bg="success">Oui</Badge>
                      ) : (
                        <Badge bg="secondary">Non</Badge>
                      )}
                    </td>
                    <td className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(t)}>
                        Modifier
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(t)}>
                        Supprimer
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Modifier formulaire" : "Creer formulaire"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Nom *</Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  isInvalid={!!errors.name}
                  placeholder="Ex: Form Tunisianet"
                />
                <Form.Control.Feedback type="invalid">
                  {Array.isArray(errors.name) ? errors.name[0] : errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group>
                <Form.Label>Fournisseur *</Form.Label>
                <Form.Select
                  value={form.fournisseur}
                  onChange={(e) => setForm((p) => ({ ...p, fournisseur: e.target.value }))}
                  isInvalid={!!errors.fournisseur}
                >
                  <option value="">-- choisir --</option>
                  {fournisseurs.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name ? `${f.name} — ${f.login}` : f.login} (id: {f.id})
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {Array.isArray(errors.fournisseur)
                    ? errors.fournisseur[0]
                    : errors.fournisseur}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={2} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                label="Actif"
                checked={Boolean(form.is_active)}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
            </Col>
          </Row>

          <hr />

          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <b>Questions du formulaire</b>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Ajoute les questions dans l'ordre. Les details techniques sont geres automatiquement.
              </div>
            </div>
            <Button variant="outline-success" onClick={addField}>
              Ajouter une question
            </Button>
          </div>

          {errors.fields && <Alert variant="danger">{errors.fields}</Alert>}

          {form.fields.map((f, i) => {
            const countOptions = form.fields.filter(
              (candidate, candidateIndex) =>
                candidateIndex !== i &&
                candidate.type === "number" &&
                candidate.key
            );

            return (
            <Card key={i} className="mb-3 border">
              <Card.Body>
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Question *</Form.Label>
                      <Form.Control
                        value={f.label}
                        onChange={(e) => {
                          const label = e.target.value;
                          updateField(i, {
                            label,
                            key: slugKey(label),
                          });
                        }}
                        placeholder="Ex: Nombre d'articles"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Type de reponse</Form.Label>
                      <Form.Select
                        value={f.type}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          updateField(i, {
                            type: nextType,
                            optionsText:
                              nextType === "select" || nextType === "radio"
                                ? f.optionsText || ""
                                : "",
                            count_from:
                              nextType === "repeatable_group" ? f.count_from || "" : "",
                            fields:
                              nextType === "repeatable_group" ? f.fields || [] : [],
                          });
                        }}
                      >
                        {FIELD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Form.Select>
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        {fieldTypeHelp(f.type)}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Obligatoire</Form.Label>
                      <div>
                        <Form.Check
                          checked={Boolean(f.required)}
                          onChange={(e) =>
                            updateField(i, { required: e.target.checked })
                          }
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={3} className="text-end">
                    <Button variant="outline-danger" onClick={() => removeField(i)}>
                      ✖ Supprimer
                    </Button>
                  </Col>
                </Row>

                {(f.type === "select" || f.type === "radio") && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Options</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={f.optionsText}
                          onChange={(e) =>
                            updateField(i, { optionsText: e.target.value })
                          }
                          placeholder={"4 mois\n6 mois\n8 mois"}
                        />
                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                          Une ligne par option. Exemple : Rouge, Bleu, Noir.
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {f.type === "repeatable_group" && (
                  <Card className="mt-3 bg-light border-0">
                    <Card.Body>
                      <Row className="g-3 align-items-end">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Question qui donne le nombre de lignes *</Form.Label>
                            <Form.Select
                              value={f.count_from || ""}
                              onChange={(e) =>
                                updateField(i, { count_from: e.target.value })
                              }
                            >
                              <option value="">-- choisir une question de type Nombre --</option>
                              {countOptions.map((option) => (
                                <option key={option.key} value={option.key}>
                                  {option.label || option.key}
                                </option>
                              ))}
                            </Form.Select>
                            {!countOptions.length && (
                              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                                Ajoute d'abord un champ de type Nombre.
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={8} className="text-end">
                          <Button
                            variant="outline-primary"
                            onClick={() => addChildField(i)}
                          >
                            Ajouter une question dans la liste
                          </Button>
                        </Col>
                      </Row>

                      <div className="mt-3">
                        {!f.fields?.length ? (
                          <Alert variant="warning" className="mb-0">
                            Cette liste ne contient encore aucune question.
                          </Alert>
                        ) : (
                          f.fields.map((child, childIndex) => (
                            <Card key={childIndex} className="mb-3">
                              <Card.Body>
                                <Row className="g-3 align-items-end">
                                  <Col md={4}>
                                    <Form.Group>
                                      <Form.Label>Question *</Form.Label>
                                      <Form.Control
                                        value={child.label}
                                        onChange={(e) => {
                                          const label = e.target.value;
                                          updateChildField(i, childIndex, {
                                            label,
                                            key: slugKey(label),
                                          });
                                        }}
                                        placeholder="Ex: Référence & couleur"
                                      />
                                    </Form.Group>
                                  </Col>

                                  <Col md={3}>
                                    <Form.Group>
                                      <Form.Label>Type de reponse</Form.Label>
                                      <Form.Select
                                        value={child.type}
                                        onChange={(e) =>
                                          updateChildField(i, childIndex, {
                                            type: e.target.value,
                                            optionsText:
                                              e.target.value === "select" ||
                                              e.target.value === "radio"
                                                ? child.optionsText || ""
                                                : "",
                                          })
                                        }
                                      >
                                        {SIMPLE_FIELD_TYPES.map((t) => (
                                          <option key={t.value} value={t.value}>
                                            {t.label}
                                          </option>
                                        ))}
                                      </Form.Select>
                                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                        {fieldTypeHelp(child.type)}
                                      </div>
                                    </Form.Group>
                                  </Col>

                                  <Col md={2}>
                                    <Form.Group>
                                      <Form.Label>Obligatoire</Form.Label>
                                      <div>
                                        <Form.Check
                                          checked={Boolean(child.required)}
                                          onChange={(e) =>
                                            updateChildField(i, childIndex, {
                                              required: e.target.checked,
                                            })
                                          }
                                        />
                                      </div>
                                    </Form.Group>
                                  </Col>

                                  <Col md={3} className="text-end">
                                    <Button
                                      variant="outline-danger"
                                      onClick={() => removeChildField(i, childIndex)}
                                    >
                                      ✖ Supprimer
                                    </Button>
                                  </Col>
                                </Row>

                                {(child.type === "select" || child.type === "radio") && (
                                  <Row className="mt-3">
                                    <Col md={12}>
                                      <Form.Group>
                                        <Form.Label>Options</Form.Label>
                                        <Form.Control
                                          as="textarea"
                                          rows={2}
                                          value={child.optionsText}
                                          onChange={(e) =>
                                            updateChildField(i, childIndex, {
                                              optionsText: e.target.value,
                                            })
                                          }
                                          placeholder={"Rouge\nBleu\nNoir"}
                                        />
                                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                          Une ligne par option.
                                        </div>
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                )}
                              </Card.Body>
                            </Card>
                          ))
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Card.Body>
            </Card>
          );
          })}

          {errors.detail && <Alert variant="danger">{errors.detail}</Alert>}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
